import {
  Component,
  ElementRef,
  HostListener,
  input,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { OpenableMenusService } from '@observation/services/openable.menu.service';
import { ScreenshotPublisherService } from '@observation/services/screenshot-publisher.service';
import {
  OpenableMenu,
  SidenavSide,
} from '@observation/shared/components/sidenav/sidenav-side.enum';
import { UserSettingsStateService } from '@services/user-settings.state.service';
import { APP_CONFIG, THEME_ICONS, THEMES } from '@shared/config';
import { ICONS, MICROPHONE_ICONS } from '@shared/config/settings.config';
import {
  BrandingService,
  OrUserSettingsService,
  ToastrService,
} from '@shared/services';
import { SessionService } from '@security/session.service';
import { LocalStorageService } from '@shared/services/local-storage.service';
import { PowerbiReportClickService } from '@shared/services/pbi-report-click.service';
import { VoiceCommandService } from '@shared/services/voice-command.service';
import { VoiceRecognitionService } from '@shared/services/voice-recognition.service';
import { usageTracking } from '@shared/usage-tracking/decorators/usage-tracking.decorator';
import { ACTION_TYPES } from '@shared/usage-tracking/usage-tracking.config';
import { of, Subject } from 'rxjs';
import { catchError, debounceTime, filter, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'od-settings-menu',
  templateUrl: './settings-menu.component.html',
  styleUrls: ['./settings-menu.component.scss'],
  standalone: false,
})
export class SettingsMenuComponent implements OnInit, OnDestroy {
  private readonly toastr = inject(ToastrService);
  private readonly sessionService = inject(SessionService);
  private readonly brandingService = inject(BrandingService);
  private readonly screenshotPublisher = inject(ScreenshotPublisherService);
  private readonly openableMenusService = inject(OpenableMenusService);
  private readonly userSettingService = inject(OrUserSettingsService);
  private readonly voiceCommandService = inject(VoiceCommandService);
  public readonly voiceRecognitionService = inject(VoiceRecognitionService);
  private readonly powerbiReportClickService = inject(
    PowerbiReportClickService
  );
  private readonly userSettingsStateService = inject(UserSettingsStateService);

  public readonly orAdminUrl = inject(APP_CONFIG).orAdminUrl;
  public readonly speechRecognitionEnabled =
    inject(APP_CONFIG).speechRecognitionEnabled;

  @ViewChild('panel', { read: ElementRef }) public panel: ElementRef;
  readonly showSpeech = input(false);

  readonly position = input(SidenavSide.LEFT);
  positionRight = false;

  isOpen = false;

  canAccessAdmin = false;
  username = '';

  public ICONS = ICONS;
  public MICROPHONE_ICONS = MICROPHONE_ICONS;
  public THEME_ICONS = THEME_ICONS;
  public THEMES = THEMES;
  public themeName: THEMES;

  private AUTOCLOSE_SECONDS: number = 15;
  private autoclose = new Subject<void>();

  public isVoiceActive$ = this.voiceRecognitionService.isListening$;

  public prepareForScreenshot$ = this.screenshotPublisher.prepareForScreenshot$;
  private subscriptions = new Subject<void>();

  @HostListener('document:pointerup', ['$event', '$event.target'])
  pointerUp(_event: MouseEvent, targetElement: HTMLElement) {
    if (!this.panel?.nativeElement.contains(targetElement) && this.isOpen) {
      this.closeSettings();
    }
  }

  ngOnInit(): void {
    this.positionRight = this.position() === SidenavSide.RIGHT;

    this.username = LocalStorageService.getEmail();

    this.canAccessAdmin =
      this.sessionService.isAuthor() || this.sessionService.isAdmin();

    this.userSettingsStateService.theme$
      .pipe(takeUntil(this.subscriptions))
      .subscribe((themeName: THEMES) => {
        this.themeName = themeName;
      });

    this.powerbiReportClickService.reportClicked$
      .pipe(takeUntil(this.subscriptions))
      .subscribe(() => {
        this.closeSettings();
      });

    this.openableMenusService.menuOpened$
      .pipe(
        takeUntil(this.subscriptions),
        filter(
          menu =>
            menu.menuType !== OpenableMenu.SETTINGS ||
            menu.position !== this.position()
        )
      )
      .subscribe(() => {
        this.closeSettings();
      });

    this.autoclose
      .asObservable()
      .pipe(
        takeUntil(this.subscriptions),
        debounceTime(this.AUTOCLOSE_SECONDS * 1000)
      )
      .subscribe(() => this.closeSettings());
  }

  ngOnDestroy() {
    this.subscriptions.next();
    this.subscriptions.unsubscribe();
  }

  onAnyPanelTap() {
    this.autoclose.next();
  }

  closeSettings() {
    this.isOpen = false;
  }
  @usageTracking(ACTION_TYPES.settingsButtonClicked)
  toggleSettings() {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.openableMenusService.setMenuOpened(
        this.position(),
        OpenableMenu.SETTINGS
      );
    }
  }

  @usageTracking(ACTION_TYPES.activateMicrophoneButtonClicked)
  public activateMicrophone(): void {
    const permissionName = 'microphone' as PermissionName;
    navigator.permissions.query({ name: permissionName }).then(result => {
      if (result.state === 'denied') {
        this.toastr.error(
          this.toastr.MESSAGE.permissions.error.microphone,
          this.toastr.TITLE.permissions.denied
        );
        return;
      }

      this.userSettingService
        .changeSpeech(true)
        .pipe(
          catchError(() => {
            this.toastr.error(this.toastr.MESSAGE.userSettings.error.update);
            return of({});
          })
        )
        .subscribe(() => {
          this.isOpen = !this.isOpen;
          this.voiceCommandService.activate();
        });
    });
  }

  @usageTracking(ACTION_TYPES.deactivateMicrophoneButtonClicked)
  public deactivateMicrophone(): void {
    this.userSettingService
      .changeSpeech(false)
      .pipe(
        catchError(() => {
          this.toastr.error(this.toastr.MESSAGE.userSettings.error.update);
          return of({});
        })
      )
      .subscribe(() => (this.isOpen = !this.isOpen));
  }

  @usageTracking(ACTION_TYPES.switchThemeButtonClicked)
  setBrandingSettings() {
    const currentTheme = this.themeName;
    const nextTheme = this.brandingService.nextTheme(currentTheme);
    this.userSettingService
      .changeTheme(nextTheme)
      .pipe(
        catchError(() => {
          this.toastr.error(this.toastr.MESSAGE.userSettings.error.update);
          return of({});
        })
      )
      .subscribe(() => (this.isOpen = !this.isOpen));
  }

  @usageTracking(ACTION_TYPES.adminButtonClicked)
  openAdmin(event: PointerEvent) {
    event.preventDefault();
    this.isOpen = !this.isOpen;
    window.location.href = this.orAdminUrl;
  }

  @usageTracking(ACTION_TYPES.logoutButtonClicked)
  logout() {
    this.isOpen = !this.isOpen;
    this.sessionService.logout(true);
  }
}
